import * as Sentry from "@sentry/browser";
import * as _ from "lodash-es";
import { DateTime } from "luxon";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_SortingState,
  type MRT_TableOptions
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiResponse } from "../../../../api";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { convertApiErrorsToMrtErrors } from "../../../../mantine/mrt/utils/convertApiErrorsToMrtErrors";
import { useToast } from "../../../../mantine/notifications/useToast";
import { MultiConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import type {
  PPAInvoiceNumberRange,
  PPAInvoiceNumberRangeFormValues,
  PPAInvoiceSupplier
} from "../PPAInvoiceNumberRangesPage.types";
import { getCounterStartOffsetColumn } from "./columns/getCounterStartOffsetColumn";
import { getFormatColumn } from "./columns/getFormatColumn";
import { getSupplierColumn } from "./columns/getSupplierColumn";
import { getValidFromColumn } from "./columns/getValidFromColumn";
import { validatePPAInvoiceNumberRangeFormValues } from "./utils/validatePPAInvoiceNumberRangeFormValues";

const DEFAULT_SORTED: MRT_SortingState = [
  {
    id: "valid_from",
    desc: true
  }
];

interface PPAInvoiceNumberRangesTableProps {
  invoiceNumberRanges: Array<PPAInvoiceNumberRange>;
  suppliers: Array<PPAInvoiceSupplier>;
  onDelete: (rangeId: string) => Promise<void>;
  onSubmit: (
    updatedRange: Omit<PPAInvoiceNumberRange, "id" | "counter_reset_interval">,
    invoiceRangeId?: string
  ) => Promise<ApiResponse>;
}

function PPAInvoiceNumberRangesTable({
  invoiceNumberRanges,
  suppliers: invoiceCompanies,
  onDelete,
  onSubmit
}: PPAInvoiceNumberRangesTableProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [rangesToBeDeleted, setRangesToBeDeleted] = useState<
    Array<PPAInvoiceNumberRange>
  >([]);

  const columns = useMemo<Array<MRT_ColumnDef<PPAInvoiceNumberRange>>>(
    () => [
      getSupplierColumn(
        { validationErrors, setValidationErrors, t },
        invoiceCompanies
      ),
      getFormatColumn({ validationErrors, setValidationErrors, t }),
      getCounterStartOffsetColumn({ validationErrors, setValidationErrors, t }),
      getValidFromColumn({ validationErrors, setValidationErrors, t })
    ],
    [invoiceCompanies, t, validationErrors]
  );

  const handleCreateRange: MRT_TableOptions<PPAInvoiceNumberRange>["onCreatingRowSave"] =
    async function ({ values, exitCreatingMode }) {
      await createRange(values, exitCreatingMode);
    };

  async function createRange(
    values: MRT_Row<PPAInvoiceNumberRangeFormValues>["_valuesCache"],
    exitCreatingMode: () => void,
    rangeId?: string
  ) {
    const newValidationErrors = validatePPAInvoiceNumberRangeFormValues(
      values,
      t
    );

    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    setIsSaving(true);
    setValidationErrors({});

    const supplier = invoiceCompanies?.find(
      (company) => company.name === values.supplier
    )?.id;

    if (!supplier) {
      const error = t("errors.NumberRanges.missingSupplierError");

      setValidationErrors({
        supplier: error
      });
      showErrorToast(error);
      setIsSaving(false);

      Sentry.captureMessage(
        `${error} in PPAInvoiceNumberRangesTable for supplier ${values.supplier}`
      );

      return;
    }

    try {
      await onSubmit(
        {
          counter_start_offset: Number(values.counter_start_offset),
          format: values.format,
          supplier_id: supplier,
          valid_from:
            (values.valid_from instanceof Date
              ? DateTime.fromJSDate(values.valid_from).toISODate()
              : values.valid_from) ?? ""
        },
        rangeId
      );
    } catch (error) {
      if (error.response.status === 400) {
        const validationErrors = convertApiErrorsToMrtErrors(
          error.response.data
        );

        if (Object.keys(validationErrors).length > 0) {
          setValidationErrors(validationErrors);
        } else {
          showErrorToast(error);
        }

        setIsSaving(false);
        return;
      }
    }

    exitCreatingMode();
    setIsSaving(false);
  }

  const defaultOptions = getDefaultMRTOptions<PPAInvoiceNumberRange>({
    emptyRowsFallbackText:
      "Es gibt noch keine personalisierten Rechnungsnummernkreise.",
    searchTextInput: {
      placeholder: "Suche nach Rechnungsnummernkreisen"
    },
    initialState: {
      showGlobalFilter: true,
      sorting: DEFAULT_SORTED
    },
    hideBorder: true
  });

  const rowEditModeOptions =
    getDefaultMRTRowEditModeOptions<PPAInvoiceNumberRange>({
      rowActions: {
        delete: {
          text: "Rechnungsnummernkreis löschen",
          onClick: (row) => setRangesToBeDeleted([row.original])
        }
      }
    });

  const tableOptions = _.merge({}, defaultOptions, rowEditModeOptions, {
    ...getMRTTopToolbarCustomActionsRenderer<PPAInvoiceNumberRange>({
      create: {
        text: "Rechnungsnummernkreis hinzufügen"
      },
      delete: {
        onClick: (rowSelection) =>
          setRangesToBeDeleted(
            invoiceNumberRanges.filter(
              (range) => range.id && rowSelection[range.id]
            )
          )
      }
    }),
    state: {
      isSaving: isSaving || isDeleting
    },
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateRange
  });

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data: invoiceNumberRanges
  });

  const namesOfRangesToBeDeleted = rangesToBeDeleted.map((range) => {
    const supplierName = invoiceCompanies?.find(
      (company) => company.id.toString() === range.supplier_id
    )?.name;

    return range.format
      ? `${supplierName ? supplierName + ": " : ""}${range.format}`
      : "Unbekannt";
  });
  const denOrDie = rangesToBeDeleted.length === 1 ? "den" : "die";
  const singleOrPluralText =
    rangesToBeDeleted.length === 1
      ? "Rechnungsnummernkreis"
      : "Rechnungsnummernkreise";

  return (
    <div className="PPAInvoiceNumberRangesTable">
      <MantineReactTable table={table} />
      <MultiConfirmationModal
        actionName="löschen"
        actionObjects={namesOfRangesToBeDeleted}
        confirmationText={`Möchten Sie ${denOrDie} folgenden ${singleOrPluralText} löschen? Dieser Schritt kann nicht rückgängig gemacht werden.`}
        isModalOpen={rangesToBeDeleted.length > 0}
        objectName={singleOrPluralText}
        toggleModal={() => setRangesToBeDeleted([])}
        onAction={async () => {
          setIsDeleting(true);

          await Promise.all(
            rangesToBeDeleted.flatMap((range) =>
              range.id ? [onDelete(range.id)] : []
            )
          );

          setRangesToBeDeleted([]);
          table.setRowSelection({}); // MRT doesn't auto-clear the selected rows after deletion
          setIsDeleting(false);
        }}
      />
    </div>
  );
}

export { PPAInvoiceNumberRangesTable, PPAInvoiceNumberRangesTableProps };
